import Big from 'big.js'
import { AccountHistoryRawResponse } from './query-params'
import { assetsList } from 'abis/set/asset-icons'
import { getPricesStore, timestampToDayIndex } from 'services/price-history/utils'
import { assetConfig } from 'services/price-history/config'
import { getProductPortfolio } from 'services/query-v2/products/index/daily-statistics/utils'
import { Nullable, assertMultiply } from 'utils'
import { getAllProductAddresses, getProductByAddress } from 'indices-list'
import { Address, isAddressEqual } from 'viem'
import { ChainAsset } from 'services/price-history/interfaces'

/**
 * ===========================================================================
 * product models
 * ===========================================================================
 */
export interface RawProduct {
  id: string
  symbol: string
  basePrice: string
  decimals: string
  marketCap: string
}

export type RawUserIndexHistoriesResponse = {
  userIndexHistories: RawIndexHistory[]
}

export type RawUserVaultHistoriesResponse = {
  userSVHistories: RawVaultHistory[]
}

export interface RawIndexHistory {
  index: RawProduct
  balance: string
  investedCapital: string
  totalSupply: string
  timestamp: number
}

export interface RawVaultHistory {
  vault: RawProduct
  balance: string
  investedCapital: string
  totalSupply: string
  timestamp: number
}

export interface Product {
  id: string
  symbol: string
  basePrice: Big
  decimals: number
  marketCap: Big
}

export interface UserPortfolioHistoryItem {
  product: Product
  balance: Big
  investedValue: Big
  totalSupply: Big
  timestamp: number
}

export interface UserProductHistory {
  id: string
  history: UserPortfolioHistoryItem[]
}

export const parseUserIndexHistory = (
  response: Nullable<AccountHistoryRawResponse>,
): (UserPortfolioHistoryItem | undefined)[] | null => {
  const assetsPrices = getPricesStore()
  const allSupportableAddresses = getAllProductAddresses()
  if (response && response !== null) {
    if (response.history === null) {
      return null
    }
    const history = response.history.filter((his) =>
      allSupportableAddresses.some((prod) => isAddressEqual(his.index as Address, prod)),
    )

    const data = history.map((historyItem) => {
      const { network } = getProductByAddress(historyItem.index)
      const productKey = `${network}:${historyItem.index}`
      if (assetsPrices[productKey]) {
        const productPortfolio = getProductPortfolio(productKey)
        const dayIndex = timestampToDayIndex(assetConfig, +historyItem.timestamp)
        const intersection = productPortfolio?.history.find(
          (history) => historyItem.timestamp === history.timestamp,
        )
        let intermediateCap = Big(0)
        if (intersection !== undefined) {
          for (const asset of intersection.assets) {
            const assetKey = `${asset.chainID}:${asset.asset}` as ChainAsset
            const assetPrice = assetsPrices[assetKey]
            if (assetPrice !== undefined) {
              assertMultiply(asset.balance, assetPrice[dayIndex])
              intermediateCap = intermediateCap.add(
                Big(asset.balance).mul(assetsPrices[assetKey][dayIndex]),
              )
            }
          }
        }

        const productPrices = assetsPrices[productKey]
        const productPricesArray = Object.values(productPrices)
        const currentProductPrice = productPricesArray[productPricesArray.length - 1]
        const productPrice = productPrices[dayIndex]
        assertMultiply(historyItem.balance, currentProductPrice)

        return {
          product: {
            id: historyItem.index,
            symbol: assetsList[historyItem.index].symbol,
            basePrice: Big(!productPrice || productPrice === '0' ? 1 : productPrice),
            decimals: 18, //FIXME
            marketCap: intermediateCap,
          },
          investedValue: Big(historyItem.balance).mul(currentProductPrice),
          balance: Big(historyItem.balance ?? 0),
          totalSupply: Big(intersection?.totalSupply ?? 0),
          timestamp: +historyItem.timestamp,
        }
      }
    })

    return data
  } else {
    return []
  }
}

// add a wrapper around these parse functions to prevent
export interface ProductBalance {
  id: string
  balance: Big
}

export interface ProductTokenBalance {
  id: string
  tokens: Big
}
